import { CSSProperties, useState, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import useScreen from 'Hooks/useScreen'
import useBrand from 'Hooks/useBrand'
import useMaestroChat from 'Hooks/useMaestroChat'
import { styx, clsx } from 'modules/Utils'
import Colors from 'modules/Colors'
import Sty from 'modules/Sty'
import Icon from 'elements/Icon'
import ButtonSymphony from 'elements/ButtonSymphony'
import { getContentAnalysis_api, ContentAnalysisResponse, AnalyzedPost } from 'api/agentApi'
import AssistantWorkspaceActions, { ActionButton } from './AssistantWorkspaceActions'
import useSignupFlow, { WorkspaceType } from 'Hooks/useSignupFlow'

// Interface for the actual analyzed post structure being returned
interface ActualAnalyzedPost {
    postMetadata: {
        caption: string;
        username: string;
        timestamp: string;
        permalink: string;
        engagement: number;
        thumbnailUrl?: string;
    };
    analysis: {
        content_breakdown: {
            themes_topics: string[];
            video_narrative: string;
            visual_elements: string[];
        };
        detailed_summary: string;
        target_audience_analysis: {
            demographics: string[];
            psychographics: string[];
            public_figures_brands: string[];
            topics: string[];
        };
    };
}

interface AssistantWorkspaceContentAnalysisProps {
    workspaceContent: {
        type: 'contentAnalysis';
        data: {
            demoAnalysisResult?: ContentAnalysisResponse;
            isDemo?: boolean;
            scenarioId?: string;
            analysisId: string;
            isLoading?: boolean;
        };
        title?: string;
        isLoading?: boolean;
    };
    handleSendMessage: (message: string, hiddenData?: any) => void;
}

const AssistantWorkspaceContentAnalysis = ({
    workspaceContent,
    handleSendMessage
}: AssistantWorkspaceContentAnalysisProps) => {
    const { mobileView, mStyle } = useScreen()
    const [analysis, setAnalysis] = useState<ContentAnalysisResponse['analysis'] | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [selectedPost, setSelectedPost] = useState<ActualAnalyzedPost | null>(null)
    const [expandedSummaries, setExpandedSummaries] = useState<Set<string>>(new Set())
    
    // Authentication and brand hooks
    const { isAuthenticated, loginWithPopup } = useAuth0()
    const {
        currentBrand,
        temporarySessionId
    } = useBrand()
    const { chatId } = useMaestroChat()
    
    // Use the new signup flow store
    const { openSignupModal } = useSignupFlow()

    const { data } = workspaceContent

    useEffect(() => {
        fetchAnalysis()
    }, [data.analysisId, data.demoAnalysisResult]) // Added data.demoAnalysisResult to dependencies

    const fetchAnalysis = async () => {
        // Check if demo analysis data is provided (for general demo scenarios or UI tests)
        if (data?.demoAnalysisResult) {
            const demoResult = data.demoAnalysisResult
            if (demoResult.success && demoResult.analysis) {
                setAnalysis(demoResult.analysis)
            } else {
                setError('Failed to load demo content analysis')
            }
            setLoading(false)
            return
        }

        if (!data.analysisId) {
            setLoading(false)
            return
        }

        try {
            setLoading(true)
            setError(null)
            const response = await getContentAnalysis_api(data.analysisId)

            if (response.success) {
                setAnalysis(response.analysis)
            } else {
                setError('Failed to load content analysis')
            }
        } catch (error: any) {
            console.error('Error fetching content analysis:', error)

            // Handle specific error cases
            if (error.response?.status === 404) {
                setError('Content analysis not found')
            } else if (error.response?.status === 410) {
                setError('This content analysis has expired. Please run a new analysis.')
            } else {
                setError('Failed to load content analysis. Please try again.')
            }
        } finally {
            setLoading(false)
        }
    }

    const formatNumber = (num: number): string => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M'
        }
        if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K'
        }
        return num.toString()
    }

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString()
    }

    const handlePostClick = (post: ActualAnalyzedPost) => {
        setSelectedPost(post)
    }

    const handleCreateContent = (hook?: string, contentType?: string) => {
        let message = 'Help me create similar content'

        if (hook && contentType) {
            message = `Help me create ${contentType} content using the hook: "${hook}"`
        } else if (hook) {
            message = `Help me create content using the hook: "${hook}"`
        } else if (contentType) {
            message = `Help me create ${contentType} content`
        }

        handleSendMessage(message)
    }

    const toggleSummaryExpansion = (postId: string) => {
        const newExpanded = new Set(expandedSummaries)
        if (newExpanded.has(postId)) {
            newExpanded.delete(postId)
        } else {
            newExpanded.add(postId)
        }
        setExpandedSummaries(newExpanded)
    }

    const renderLoadingState = () => (
        <div className="contentAnalysisLoading" style={sty.loadingContainer}>
            <Icon
                className="contentAnalysisLoadingIcon"
                icon="loading"
                size={48}
                style={sty.loadingIcon}
            />
            <div style={sty.loadingText}>Loading content analysis...</div>
        </div>
    )

    const renderErrorState = () => (
        <div className="contentAnalysisError" style={sty.errorContainer}>
            <Icon
                className="contentAnalysisErrorIcon"
                icon="warning"
                size={48}
                style={sty.errorIcon}
            />
            <div style={sty.errorText}>{error}</div>
            <ButtonSymphony
                className="retryButton"
                variant="outlined"
                onClick={fetchAnalysis}
                style={sty.retryButton}
            >
                Try Again
            </ButtonSymphony>
        </div>
    )

    const renderProfileHeader = () => {
        if (!analysis) return null

        return (
            <div className="profileHeader" style={sty.profileHeader}>
                <div className="profileInfo" style={sty.profileInfo}>
                    <Icon className="instagramIcon" icon="instagramFlat" size={32} style={sty.instagramIcon} />
                    <div className="profileDetails" style={sty.profileDetails}>
                        <h3 style={sty.username}>@{analysis.username}</h3>
                        <div className="profileStats" style={sty.profileStats}>
                            <span>{formatNumber(analysis.profileData.followers)} followers</span>
                            <span>{analysis.profileData.postsCount} posts</span>
                        </div>
                    </div>
                </div>
                <div className="analysisStats" style={sty.analysisStats}>
                    <div style={sty.analysisStatItem}>
                        <strong>{formatDate(analysis.createdAt)}</strong>
                        <span>analyzed on</span>
                    </div>
                </div>
            </div>
        )
    }

    const renderTopPerformingContent = () => {
        if (!analysis?.recommendations) return null

        // Cast to any to work with the actual data structure
        const recommendations = analysis.recommendations as any
        const contentThemes = recommendations.contentThemes || []
        const visualElements = recommendations.visualElements || []
        const contentPatterns = recommendations.contentPatterns || []

        const showFullContent = shouldShowFullAnalysis()
        const maxItems = showFullContent ? 4 : 2

        return (
            <div className="topPerformingContent" style={sty.section}>
                <h4 style={sty.sectionTitle}>
                    <Icon className="sectionTitleIcon" icon="star" size={20} />
                    What's Working Well
                    {!showFullContent && (
                        <span style={sty.limitedBadge}>Limited Preview</span>
                    )}
                </h4>

                <div className="performingContentGrid" style={mStyle(sty, 'performingContentGrid')}>
                    {contentThemes.length > 0 && (
                        <div className="performingContentItem" style={sty.performingContentItem}>
                            <h5 style={sty.performingContentTitle}>Top Content Themes</h5>
                            <div className="themesList" style={sty.tagsList}>
                                {contentThemes.slice(0, maxItems).map((theme: string, index: number) => (
                                    <button
                                        key={index}
                                        className="themeTag"
                                        style={sty.clickableTag}
                                        onClick={() => showFullContent ? handleCreateContent(undefined, theme) : handleSignUpToUnlock()}
                                    >
                                        {theme}
                                    </button>
                                ))}
                                {!showFullContent && contentThemes.length > maxItems && (
                                    <span style={sty.hiddenItemsIndicator}>
                                        +{contentThemes.length - maxItems} more
                                    </span>
                                )}
                            </div>
                            {showFullContent ? (
                                <ButtonSymphony
                                    className="getIdeasButton"
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleCreateContent(undefined, contentThemes[0])}
                                    style={sty.getIdeasButton}
                                >
                                    <Icon className="getIdeasIcon" icon="lightbulb" size={14} />
                                    Get Ideas
                                </ButtonSymphony>
                            ) : (
                                <ButtonSymphony
                                    className="signupToUnlockButton"
                                    variant="primary"
                                    size="small"
                                    onClick={handleSignUpToUnlock}
                                    style={sty.signupToUnlockButton}
                                >
                                    <Icon className="signupIcon" icon="user" size={14} />
                                    Sign Up to Unlock
                                </ButtonSymphony>
                            )}
                        </div>
                    )}

                    {visualElements.length > 0 && showFullContent && (
                        <div className="performingContentItem" style={sty.performingContentItem}>
                            <h5 style={sty.performingContentTitle}>Best Visual Styles</h5>
                            <div className="visualsList" style={sty.tagsList}>
                                {visualElements.slice(0, 4).map((element: string, index: number) => (
                                    <span key={index} className="visualTag" style={sty.tag}>
                                        {element}
                                    </span>
                                ))}
                            </div>
                            <ButtonSymphony
                                className="getIdeasButton"
                                variant="outlined"
                                size="small"
                                onClick={() => handleCreateContent(undefined, 'visual content with ' + visualElements[0])}
                                style={sty.getIdeasButton}
                            >
                                <Icon className="getIdeasIcon" icon="lightbulb" size={14} />
                                Get Ideas
                            </ButtonSymphony>
                        </div>
                    )}

                    {contentPatterns.length > 0 && showFullContent && (
                        <div className="performingContentItem" style={sty.performingContentItem}>
                            <h5 style={sty.performingContentTitle}>Successful Patterns</h5>
                            <div className="patternsList" style={sty.tagsList}>
                                {contentPatterns.slice(0, 4).map((pattern: string, index: number) => (
                                    <span key={index} className="patternTag" style={sty.tag}>
                                        {pattern}
                                    </span>
                                ))}
                            </div>
                            <ButtonSymphony
                                className="getIdeasButton"
                                variant="outlined"
                                size="small"
                                onClick={() => handleCreateContent(contentPatterns[0])}
                                style={sty.getIdeasButton}
                            >
                                <Icon className="getIdeasIcon" icon="lightbulb" size={14} />
                                Get Ideas
                            </ButtonSymphony>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    const renderRecommendations = () => {
        if (!analysis?.recommendations) return null

        const recommendations = analysis.recommendations as any
        const actionableRecommendations = recommendations.recommendations || []
        const contentIdeas = recommendations.contentIdeas || []

        const showFullContent = shouldShowFullAnalysis()
        const maxRecommendations = showFullContent ? 6 : 3
        const maxContentIdeas = showFullContent ? 6 : 2

        return (
            <div className="recommendations" style={sty.section}>
                <h4 style={sty.sectionTitle}>
                    <Icon className="sectionTitleIcon" icon="lightbulb" size={20} />
                    Recommendations & Content Ideas
                    {!showFullContent && (
                        <span style={sty.limitedBadge}>Limited Preview</span>
                    )}
                </h4>

                <div className="recommendationsContainer" style={mStyle(sty, 'recommendationsContainer')}>
                    {actionableRecommendations.length > 0 && (
                        <div className="recommendationsColumn" style={sty.recommendationsColumn}>
                            <h5 style={sty.columnTitle}>Key Recommendations</h5>
                            <div className="recommendationsList" style={sty.recommendationsList}>
                                {actionableRecommendations.slice(0, maxRecommendations).map((rec: string, index: number) => (
                                    <div key={index} className="recommendationItem" style={sty.recommendationItem}>
                                        <div className="recommendationText" style={sty.recommendationText}>
                                            {rec}
                                        </div>
                                    </div>
                                ))}
                                {!showFullContent && actionableRecommendations.length > maxRecommendations && (
                                    <div className="unlockMoreContainer" style={sty.unlockMoreContainer}>
                                        <div style={sty.unlockMoreText}>
                                            +{actionableRecommendations.length - maxRecommendations} more recommendations
                                        </div>
                                        <ButtonSymphony
                                            className="unlockMoreButton"
                                            variant="primary"
                                            size="small"
                                            onClick={handleSignUpToUnlock}
                                            style={sty.unlockMoreButton}
                                        >
                                            <Icon className="unlockIcon" icon="user" size={14} />
                                            Sign Up to See All
                                        </ButtonSymphony>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {contentIdeas.length > 0 && (
                        <div className="contentIdeasColumn" style={sty.recommendationsColumn}>
                            <h5 style={sty.columnTitle}>Content Ideas</h5>
                            <div className="contentIdeasList" style={sty.contentIdeasList}>
                                {contentIdeas.slice(0, maxContentIdeas).map((idea: string, index: number) => (
                                    <button
                                        key={index}
                                        className="contentIdeaItem"
                                        style={sty.contentIdeaItem}
                                        onClick={() => showFullContent ? handleCreateContent(idea) : handleSignUpToUnlock()}
                                    >
                                        <Icon className="ideaIcon" icon={showFullContent ? "plus" : "warning"} size={16} />
                                        {idea}
                                    </button>
                                ))}
                                {!showFullContent && contentIdeas.length > maxContentIdeas && (
                                    <div className="moreIdeasTeaser" style={sty.moreIdeasTeaser}>
                                        <div style={sty.moreIdeasText}>
                                            +{contentIdeas.length - maxContentIdeas} more content ideas available
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    const renderTopPosts = () => {
        if (!analysis?.analyzedPosts?.length) return null

        const showFullContent = shouldShowFullAnalysis()
        const maxPosts = showFullContent ? 5 : 2

        // Cast to any to work with the actual data structure
        const posts = analysis.analyzedPosts as any[]
        const topPosts = posts
            .filter(post => post.postMetadata && post.analysis)
            .sort((a, b) => (b.postMetadata?.engagement || 0) - (a.postMetadata?.engagement || 0))
            .slice(0, maxPosts)

        const totalPosts = posts.filter(post => post.postMetadata && post.analysis).length

        return (
            <div className="topPosts" style={sty.section}>
                <h4 style={sty.sectionTitle}>
                    <Icon className="sectionTitleIcon" icon="heart" size={20} />
                    Top {showFullContent ? '5' : '2'} Performing Posts
                    {!showFullContent && (
                        <span style={sty.limitedBadge}>Limited Preview</span>
                    )}
                </h4>

                <div className="postsGrid" style={mStyle(sty, 'postsGrid')}>
                    {topPosts.map((post: any, index: number) => {
                        const { postMetadata, analysis: postAnalysis } = post
                        const engagement = postMetadata?.engagement || 0
                        const caption = postMetadata?.caption || ''
                        const permalink = postMetadata?.permalink || ''
                        const timestamp = postMetadata?.timestamp || ''
                        const thumbnailUrl = postMetadata?.thumbnailUrl || ''
                        const detailedSummary = postAnalysis?.detailed_summary || ''
                        const postId = permalink || `post-${index}`
                        const isExpanded = expandedSummaries.has(postId)

                        // Calculate engagement rate
                        const engagementRate = analysis.profileData.followers
                            ? (engagement / analysis.profileData.followers) * 100
                            : 0

                        const shouldTruncate = detailedSummary.length > 120
                        const displaySummary = shouldTruncate && !isExpanded
                            ? detailedSummary.substring(0, 120) + '...'
                            : detailedSummary

                        return (
                            <div
                                key={postId}
                                className="postCard"
                                style={sty.postCard}
                            >
                                <div className="postHeader" style={sty.postHeader}>
                                    <div className="postHeaderContent" style={sty.postHeaderContent}>
                                        <Icon className="instagramIcon" icon="instagramFlat" size={20} />
                                        <span style={sty.postRank}>#{index + 1}</span>
                                    </div>
                                    {thumbnailUrl && (
                                        <div className="postThumbnail" style={sty.postThumbnail}>
                                            <img
                                                src={thumbnailUrl}
                                                alt="Post thumbnail"
                                                style={sty.thumbnailImage}
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none'
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="postStats" style={sty.postStatsHorizontal}>
                                    <span style={sty.statItem}>
                                        <Icon className="postStatsIcon" icon="heart" size={14} />
                                        {formatNumber(engagement)} likes
                                    </span>
                                    <span style={sty.statItem}>
                                        {engagementRate.toFixed(1)}% rate
                                    </span>
                                </div>

                                <div className="postCaption" style={sty.postCaption}>
                                    {caption.length > 80
                                        ? caption.substring(0, 80) + '...'
                                        : caption
                                    }
                                </div>

                                {detailedSummary && (
                                    <div className="postSummary" style={sty.postSummary}>
                                        <strong>What's Working:</strong> {displaySummary}
                                        {shouldTruncate && (
                                            <button
                                                className="expandButton"
                                                style={sty.expandButton}
                                                onClick={() => toggleSummaryExpansion(postId)}
                                            >
                                                {isExpanded ? 'Show less' : 'Show more'}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {postAnalysis?.content_breakdown?.themes_topics?.length > 0 && (
                                    <div className="postTopics" style={sty.postTopics}>
                                        {postAnalysis.content_breakdown.themes_topics.slice(0, 3).map((topic: string, topicIndex: number) => (
                                            <span key={topicIndex} className="postTopic" style={sty.postTopic}>
                                                {topic}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div className="postFooter" style={sty.postFooter}>
                                    <div className="postDate" style={sty.postDate}>
                                        {formatDate(timestamp)}
                                    </div>
                                    <ButtonSymphony
                                        className="postGetIdeasButton"
                                        variant="text"
                                        size="small"
                                        onClick={() => {
                                            const themes = postAnalysis?.content_breakdown?.themes_topics || []
                                            const topTheme = themes[0] || 'similar content'
                                            handleCreateContent(undefined, topTheme)
                                        }}
                                        style={sty.postGetIdeasButton}
                                    >
                                        <Icon className="postGetIdeasIcon" icon="wand" size={14} />
                                        Get Ideas
                                    </ButtonSymphony>
                                </div>
                            </div>
                        )
                    })}
                    
                    {!showFullContent && totalPosts > maxPosts && (
                        <div className="morePostsTeaser" style={sty.morePostsTeaser}>
                            <div className="morePostsContent" style={sty.morePostsContent}>
                                <Icon className="lockIcon" icon="warning" size={32} style={sty.lockIcon} />
                                <h5 style={sty.morePostsTitle}>
                                    {totalPosts - maxPosts} More Posts Available
                                </h5>
                                <div style={sty.morePostsDescription}>
                                    Sign up to see detailed analysis of all {totalPosts} posts with insights and recommendations
                                </div>
                                <ButtonSymphony
                                    className="signupForMoreButton"
                                    variant="primary"
                                    onClick={handleSignUpToUnlock}
                                    style={sty.signupForMoreButton}
                                >
                                    <Icon className="signupIcon" icon="user" size={16} />
                                    Sign Up to See All Posts
                                </ButtonSymphony>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    // Authentication and content gating logic
    const shouldShowFullAnalysis = () => {
        return isAuthenticated
    }

    const handleSignUpToUnlock = async () => {
        try {
            await openSignupModal(WorkspaceType.CONTENT_ANALYSIS, {
                additionalStorageData: data.analysisId ? { 'content-analysis-id': data.analysisId } : undefined,
                chatId: chatId || undefined,
                temporarySessionId: temporarySessionId || undefined,
                loginFunction: async () => {
                    await loginWithPopup({
                        authorizationParams: {
                            screen_hint: 'signup'
                        }
                    })
                }
            })
        } catch (error) {
            console.error('Signup failed:', error)
            // Error is handled by the store, just log it here
        }
    }

    const renderActions = () => {
        if (!analysis) return null

        // Check authentication state to determine button behavior
        if (!isAuthenticated) {
            return (
                <AssistantWorkspaceActions
                    icon="user"
                    title="Sign up to unlock full analysis"
                    description="Get complete insights, recommendations, and content ideas"
                    buttons={[
                        {
                            text: 'Sign Up to View Full Breakdown',
                            onClick: () => handleSignUpToUnlock(),
                            variant: 'primary',
                            icon: 'user'
                        }
                    ]}
                />
            )
        }

        // Authenticated user - show content creation actions
        return (
            <AssistantWorkspaceActions
                icon="lightbulb"
                title="Ready to create content?"
                description="Use these insights to improve your content strategy"
                buttons={[
                    {
                        text: 'Create Similar Content',
                        onClick: () => handleCreateContent(),
                        variant: 'primary',
                        icon: 'plus'
                    },
                    {
                        text: 'Analyze Another Account',
                        onClick: () => handleSendMessage('Analyze another Instagram account'),
                        variant: 'outlined',
                        icon: 'instagramFlat'
                    }
                ]}
            />
        )
    }

    if (loading || workspaceContent.isLoading) {
        return renderLoadingState()
    }

    if (error) {
        return renderErrorState()
    }

    if (!analysis) {
        return (
            <div className="contentAnalysisEmpty" style={sty.emptyState}>
                <Icon className="emptyIcon" icon="instagramFlat" size={48} style={sty.emptyIcon} />
                <div style={sty.emptyText}>No analysis data available</div>
            </div>
        )
    }

    return (
        <div className="contentAnalysisWorkspace" style={mStyle(sty, 'container')}>
            <div className="contentAnalysisContent" style={sty.content}>
                {renderProfileHeader()}
                {renderTopPerformingContent()}
                {renderRecommendations()}
                {renderTopPosts()}
            </div>
            {renderActions()}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: 0,
    },
    container_mobile: {
        padding: 16,
    },
    content: {
        flex: 1,
        overflowY: 'auto',
        padding: '0 0 16px 0',
    },

    // Loading and Error States
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: 16,
        padding: 40,
    },
    loadingIcon: {
        color: Colors.purple,
    },
    loadingText: {
        fontSize: 16,
        color: Colors.greyDark,
        textAlign: 'center',
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: 16,
        padding: 40,
        textAlign: 'center',
    },
    errorIcon: {
        color: Colors.red,
    },
    errorText: {
        fontSize: 16,
        color: Colors.textColor,
    },
    retryButton: {
        marginTop: 12,
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: 16,
        padding: 40,
    },
    emptyIcon: {
        color: Colors.greyDark,
    },
    emptyText: {
        fontSize: 16,
        color: Colors.greyDark,
        textAlign: 'center',
    },

    // Profile Header
    profileHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 20,
        backgroundColor: Colors.greyLighter,
        borderRadius: 12,
        marginBottom: 20,
        gap: 16,
    },
    profileInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    instagramIcon: {
        flexShrink: 0,
    },
    profileDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    username: {
        margin: 0,
        fontSize: 18,
        fontWeight: 600,
        color: Colors.textColor,
    },
    profileStats: {
        display: 'flex',
        gap: 16,
        fontSize: 14,
        color: Colors.greyDark,
    },
    analysisStats: {
        display: 'flex',
        gap: 20,
    },
    analysisStatItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        fontSize: 12,
        color: Colors.greyDark,
    },

    // Sections
    section: {
        marginBottom: 24,
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        margin: '0 0 16px 0',
        fontSize: 16,
        fontWeight: 600,
        color: Colors.white,
    },

    // Top Performing Content
    performingContentGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 16,
    },
    performingContentGrid_mobile: {
        gridTemplateColumns: '1fr',
    },
    performingContentItem: {
        padding: 16,
        backgroundColor: 'white',
        border: `1px solid ${Colors.grey}`,
        borderRadius: 8,
        position: 'relative',
    },
    performingContentTitle: {
        margin: '0 0 12px 0',
        fontSize: 14,
        fontWeight: 600,
        color: Colors.textColor,
    },
    tagsList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 12,
    },
    tag: {
        padding: '4px 8px',
        backgroundColor: Colors.greyLight,
        borderRadius: 4,
        fontSize: 12,
        color: Colors.textColor,
        border: 'none',
        cursor: 'default',
    },
    clickableTag: {
        padding: '6px 12px',
        backgroundColor: Colors.purple + '15',
        border: `1px solid ${Colors.purple}`,
        borderRadius: 20,
        fontSize: 12,
        color: Colors.purple,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontFamily: 'inherit',
    },
    getIdeasButton: {
        marginTop: 8,
        fontSize: 12,
        padding: '4px 8px',
        height: 'auto',
        minHeight: 'auto',
    },

    // Authentication Gating Styles
    limitedBadge: {
        marginLeft: 8,
        padding: '2px 8px',
        backgroundColor: '#FFA500' + '20',
        color: '#FFA500',
        borderRadius: 12,
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
    },
    hiddenItemsIndicator: {
        padding: '4px 8px',
        backgroundColor: Colors.greyLight,
        borderRadius: 4,
        fontSize: 11,
        color: Colors.greyDark,
        fontStyle: 'italic',
    },
    signupToUnlockButton: {
        marginTop: 8,
        fontSize: 12,
        padding: '4px 8px',
        height: 'auto',
        minHeight: 'auto',
        backgroundColor: Colors.purple,
        border: `1px solid ${Colors.purple}`,
    },

    // Recommendations
    recommendationsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 20,
    },
    recommendationsContainer_mobile: {
        gridTemplateColumns: '1fr',
    },
    recommendationsColumn: {
        display: 'flex',
        flexDirection: 'column',
    },
    columnTitle: {
        margin: '0 0 12px 0',
        fontSize: 14,
        fontWeight: 600,
        color: Colors.white,
    },
    recommendationsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    recommendationItem: {
        padding: 16,
        backgroundColor: 'white',
        border: `1px solid ${Colors.grey}`,
        borderRadius: 8,
    },
    recommendationText: {
        fontSize: 14,
        color: Colors.textColor,
        lineHeight: 1.4,
    },
    unlockMoreContainer: {
        padding: 16,
        backgroundColor: Colors.greyLighter,
        border: `1px solid ${Colors.grey}`,
        borderRadius: 8,
        textAlign: 'center',
        marginTop: 8,
    },
    unlockMoreText: {
        fontSize: 13,
        color: Colors.greyDark,
        marginBottom: 12,
    },
    unlockMoreButton: {
        fontSize: 12,
        padding: '6px 12px',
        height: 'auto',
        minHeight: 'auto',
    },
    contentIdeasList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    contentIdeaItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 12,
        backgroundColor: 'white',
        border: `1px solid ${Colors.grey}`,
        borderRadius: 8,
        fontSize: 13,
        color: Colors.textColor,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        textAlign: 'left',
    },
    moreIdeasTeaser: {
        padding: 12,
        backgroundColor: Colors.greyLighter,
        borderRadius: 8,
        textAlign: 'center',
        marginTop: 8,
    },
    moreIdeasText: {
        fontSize: 12,
        color: Colors.greyDark,
        fontStyle: 'italic',
    },

    // Posts Grid
    postsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 20,
    },
    postsGrid_mobile: {
        gridTemplateColumns: '1fr',
    },
    postCard: {
        backgroundColor: 'white',
        border: `1px solid ${Colors.grey}`,
        borderRadius: 12,
        padding: 16,
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    },
    postHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: 12,
        gap: 12,
    },
    postHeaderContent: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    postRank: {
        fontSize: 18,
        fontWeight: 700,
        color: Colors.purple,
    },
    postThumbnail: {
        flexShrink: 0,
        width: 60,
        height: 60,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: Colors.greyLight,
    },
    thumbnailImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    postStatsHorizontal: {
        display: 'flex',
        gap: 16,
        marginBottom: 12,
        paddingBottom: 8,
        borderBottom: `1px solid ${Colors.greyLight}`,
    },
    statItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        color: Colors.greyDark,
        fontWeight: 500,
    },
    postCaption: {
        fontSize: 13,
        color: Colors.textColor,
        lineHeight: 1.4,
        marginBottom: 12,
        fontStyle: 'italic',
    },
    postSummary: {
        padding: 12,
        backgroundColor: Colors.greyLighter,
        borderRadius: 8,
        fontSize: 12,
        color: Colors.textColor,
        lineHeight: 1.4,
        marginBottom: 12,
    },
    expandButton: {
        background: 'none',
        border: 'none',
        color: Colors.purple,
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 500,
        marginLeft: 4,
        textDecoration: 'underline',
        padding: 0,
        fontFamily: 'inherit',
    },
    postTopics: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginBottom: 12,
    },
    postTopic: {
        padding: '3px 8px',
        backgroundColor: Colors.purple + '15',
        color: Colors.purple,
        borderRadius: 12,
        fontSize: 10,
        fontWeight: 500,
    },
    postFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    postDate: {
        fontSize: 11,
        color: Colors.greyDark,
    },
    postGetIdeasButton: {
        fontSize: 11,
        padding: '4px 8px',
        minHeight: 'auto',
        height: 'auto',
    },

    // Posts Teaser Section
    morePostsTeaser: {
        backgroundColor: Colors.greyLighter,
        border: `2px dashed ${Colors.grey}`,
        borderRadius: 12,
        padding: 24,
        textAlign: 'center',
        gridColumn: '1 / -1', // Span full width
    },
    morePostsContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
    },
    lockIcon: {
        color: Colors.greyDark,
    },
    morePostsTitle: {
        margin: 0,
        fontSize: 16,
        fontWeight: 600,
        color: Colors.textColor,
    },
    morePostsDescription: {
        fontSize: 14,
        color: Colors.greyDark,
        lineHeight: 1.4,
        marginBottom: 4,
    },
    signupForMoreButton: {
        fontSize: 14,
        padding: '8px 16px',
        height: 'auto',
        minHeight: 'auto',
    },

    // Signup Modal Styles
    signupModalText: {
        fontSize: 14,
        color: Colors.textColor,
        lineHeight: 1.5,
        marginBottom: 16,
    },
    signupBenefitsList: {
        margin: '8px 0',
        paddingLeft: 20,
    },
    signupModalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 16,
    },
}

export default AssistantWorkspaceContentAnalysis 